/**
 * Classement d'un live (design Live School) : pastille de rang (or, argent,
 * bronze), avatar, nom, score ; ma ligne en bleu. Et la carte « Ta position ».
 * Partagé par le classement d'un défi privé, les classements Live School
 * (National, Niveau, Région…) et l'écran de fin.
 */

import { Crown, Trophy } from 'lucide-react';
import { AvatarCircle, cn } from '@jeraisonne/ui';

export interface RankingRow {
  key: string;
  rank: number;
  name: string;
  avatarUrl: string | null;
  score: number;
  isMe: boolean;
  /** « Invité »… */
  tag?: string | null;
}

/** « 1 240 » (espace fine). PUR. */
export function formatScore(n: number): string {
  return Math.round(n).toLocaleString('fr-FR');
}

/** Rang exprimé en « top N % » (au moins 1 %). PUR. */
export function topPercent(rank: number, total: number): number {
  if (total <= 0 || rank <= 0) return 100;
  return Math.min(100, Math.max(1, Math.ceil((rank / total) * 100)));
}

const MEDAL_CLASS: Record<number, string> = {
  1: 'bg-[#f4c64e] text-[#0a2e57] shadow-[0_2px_8px_rgba(244,198,78,0.5)]',
  2: 'bg-[#cfd8e6] text-[#0a2e57]',
  3: 'bg-[#d08a4a] text-white',
};

export function RankBadge({ rank, me = false }: { rank: number; me?: boolean }) {
  const medal = MEDAL_CLASS[rank];
  return (
    <span
      aria-label={`Rang ${rank}`}
      className={cn(
        'font-display flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-extrabold tabular-nums',
        medal ?? (me ? 'bg-[rgba(255,255,255,0.25)] text-white' : 'text-ink-500'),
      )}
    >
      {rank}
    </span>
  );
}

export function RankingList({
  rows,
  unit,
  paper = false,
  className,
}: {
  rows: RankingRow[];
  /** « HORA », « pts ». */
  unit: string;
  /** Feuille claire posée sur l'écran sombre d'un live. */
  paper?: boolean;
  className?: string;
}) {
  return (
    <ol
      data-testid="ranking-list"
      className={cn(
        'space-y-1 rounded-3xl p-2',
        paper
          ? 'theme-paper bg-white shadow-[0_14px_34px_rgba(0,6,24,0.35)]'
          : 'ring-ink-100 bg-white shadow-sm ring-1',
        className,
      )}
    >
      {rows.map((r) => (
        <li
          key={r.key}
          className={cn(
            'flex min-h-14 items-center gap-3 rounded-2xl px-2.5 py-2',
            r.isMe &&
              'bg-linear-to-r from-[#2f86ff] to-[#0566f2] text-white shadow-[0_6px_18px_rgba(5,102,242,0.35)]',
          )}
        >
          <RankBadge rank={r.rank} me={r.isMe} />
          <span className="relative shrink-0">
            {r.rank === 1 && (
              <Crown
                aria-hidden
                className="absolute -top-3 left-1/2 h-4 w-4 -translate-x-1/2 fill-[#f4c64e] text-[#f4c64e]"
              />
            )}
            <AvatarCircle name={r.name} src={r.avatarUrl} size="sm" />
          </span>
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm font-semibold',
              r.isMe ? 'text-white' : 'text-ink-900',
            )}
          >
            {r.name}
            {r.isMe && <span className="font-normal opacity-80"> (toi)</span>}
            {r.tag && (
              <span
                className={cn(
                  'ml-1.5 rounded-full px-1.5 py-0.5 text-xs font-medium',
                  r.isMe ? 'bg-[rgba(255,255,255,0.2)]' : 'bg-ink-100 text-ink-600',
                )}
              >
                {r.tag}
              </span>
            )}
          </span>
          <span
            className={cn(
              'font-display shrink-0 text-sm font-extrabold tabular-nums',
              r.isMe ? 'text-white' : 'text-primary-600',
            )}
          >
            {formatScore(r.score)}{' '}
            <span className={cn('text-xs font-bold', r.isMe ? 'text-white/80' : 'text-ink-400')}>
              {unit}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

/** « Ta position : #12 sur 54 · top 22 % » avec une jauge. */
export function MyPositionCard({
  rank,
  total,
  className,
}: {
  rank: number;
  total: number;
  className?: string;
}) {
  const pct = topPercent(rank, total);
  const fill = total > 0 ? Math.max(4, Math.round(((total - rank + 1) / total) * 100)) : 0;
  return (
    <div
      data-testid="my-position"
      className={cn(
        'ring-ink-100 flex items-center gap-3 rounded-3xl bg-white p-4 shadow-sm ring-1',
        className,
      )}
    >
      <span className="bg-linear-to-b flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl from-[#2f86ff] to-[#0566f2] text-white shadow-[0_6px_16px_rgba(5,102,242,0.35)]">
        <Trophy className="h-6 w-6" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-ink-500 text-xs font-semibold uppercase tracking-wider">Ta position</p>
        <p className="font-display text-ink-900 text-xl font-extrabold leading-tight">
          #{rank} <span className="text-ink-500 text-sm font-semibold">sur {total}</span>
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          <div className="bg-ink-100 h-2 flex-1 overflow-hidden rounded-full">
            <div
              className="bg-linear-to-r h-full rounded-full from-[#2f86ff] to-[#f4c64e]"
              style={{ width: `${fill}%` }}
            />
          </div>
          <span className="text-ink-600 shrink-0 text-xs font-semibold">{`Top ${pct}\u00a0%`}</span>
        </div>
      </div>
    </div>
  );
}
